// payerPick.js — PROD-187 (P12): the ONE owner of the payer key set a carrier re-pick writes.
//
// ★★ WHY THIS EXISTS. The server does not clear a payer id because the client sent ''.
//   PUT /profile shallow-merges the insurance section over the stored row and then calls
//   ensure_stedi_payer_id, which returns early whenever the merged record still carries a
//   routable id under payerId, payer_id or providerId, and otherwise re-resolves the
//   carrier NAME. So a re-pick that sends only some keys leaves the old payer's id under
//   the keys it left out (stediPayerId, primaryPayerId, payer_id), and a re-pick that
//   leaves a routable id in providerId next to a cleared payerId keeps routing to the
//   old payer. Measured against the server's own functions, not a restatement of them.
//
// The contract every writer here meets (plans/p_2026-09-28/ORELLA_PLAN_P12_2026-09-28.md, K1-K6):
//   K1  a re-pick carries the WHOLE key set below plus providerId and the new provider
//       name. Cleared values are '' / false / []; no key is ever undefined.
//   K2  when payerId is falsy, providerId is a non-routable slug or 'custom'.
//   K3  a discovery apply writes payerId = cand.payerId, clears stediPayerId and
//       primaryPayerId, and sets providerId = cand.carrierId || 'custom' (never the old one).
//   The success criterion is on the stored row after merge + ensure: no key still holds
//   the previous pick's id. scripts/check-payer-repick.mjs holds every arm of it.
//
// No imports on purpose: gates import this file directly under plain node.

export const PAYER_KEYS = Object.freeze([
  'payerId',
  'payer_id',
  'stediPayerId',
  'primaryPayerId',
  'payerName',
  'stediPayerName',
  'payerMatchConfidence',
  'payerMatchSource',
  'payerNeedsConfirmation',
  'payerCandidates',
  // ensure_stedi_payer_id stamps it with the resolved payer's eligibility support. No
  // screen reads it today; it is cleared with the rest so it cannot describe the old payer.
  'stediPayerEligibility',
])

const str = (v) => (v === undefined || v === null ? '' : String(v)).trim()

function cleared() {
  return {
    payerId: '',
    payer_id: '',
    stediPayerId: '',
    primaryPayerId: '',
    payerName: '',
    stediPayerName: '',
    payerMatchConfidence: '',
    payerMatchSource: '',
    payerNeedsConfirmation: false,
    payerCandidates: [],
    stediPayerEligibility: '',
  }
}

// Every payer key at its cleared value. Frozen; spread it, never mutate it.
export const PAYER_CLEARED = Object.freeze({ ...cleared(), payerCandidates: Object.freeze([]) })

// JS mirror of rcm_service._has_passthrough_payer_id applied to ONE value: a numeric CMS
// id of 4-6 digits, or a short all-caps Stedi id with no whitespace (Python's isupper():
// at least one cased character and no lower-case one). check-payer-repick PR-3 pins this
// to a table the real server function produced; a drift there is a red.
export function isRoutablePayerId(v) {
  const s = str(v)
  if (/^[0-9]+$/.test(s) && s.length >= 4 && s.length <= 6) return true
  if (s.length >= 2 && s.length <= 8 && !/\s/.test(s)
      && s === s.toUpperCase() && s !== s.toLowerCase()) return true
  return false
}

// A registry slug may sit in providerId only while it cannot route: a slug the server
// would read as a payer id is replaced by 'custom' (K2).
function safeSlug(slug) {
  const s = str(slug)
  return s && !isRoutablePayerId(s) ? s : 'custom'
}

/**
 * payerPick({ kind, ... }) -> { provider, providerId, ...every PAYER_KEYS key }
 *   kind 'registry'  : { stediId, primaryPayerId, name, order: 'stedi-first'|'primary-first', source?, eligibility? }
 *                      The id order is the caller's: Profile routes stedi-first and
 *                      onboarding primary-first, exactly as before this owner existed.
 *   kind 'typed'     : { name, slug? }  the patient's own words, not a resolved payer.
 *   kind 'alternate' : { payerId, name, eligibility? }  one of the server's payerCandidates.
 *   kind 'discovery' : { payerId, name, slug }  a discovered candidate (see candidatePayerPick).
 *   kind 'clear'     : self-pay / HSA / "Change": no carrier at all.
 * Never returns undefined for any key.
 */
export function payerPick(opts = {}) {
  const kind = opts.kind
  const out = cleared()
  if (kind === 'clear') return { provider: '', providerId: '', ...out }
  const name = str(opts.name)
  if (kind === 'registry') {
    const stedi = str(opts.stediId)
    const primary = str(opts.primaryPayerId)
    const routing = opts.order === 'primary-first' ? (primary || stedi) : (stedi || primary)
    if (!routing) return payerPick({ kind: 'typed', name, slug: opts.slug })
    return {
      provider: name,
      providerId: routing,
      ...out,
      payerId: routing,
      stediPayerId: stedi,
      primaryPayerId: primary,
      payerName: name,
      stediPayerName: name,
      payerMatchConfidence: 'high',
      payerMatchSource: str(opts.source) || 'patient_pick',
      payerNeedsConfirmation: false,
      stediPayerEligibility: str(opts.eligibility),
    }
  }
  if (kind === 'alternate') {
    const pid = str(opts.payerId)
    if (!pid) return payerPick({ kind: 'typed', name })
    return {
      provider: name,
      providerId: pid,
      ...out,
      payerId: pid,
      payerName: name,
      stediPayerName: name,
      payerMatchConfidence: 'high',
      payerMatchSource: 'patient_pick',
      payerNeedsConfirmation: false,
      stediPayerEligibility: str(opts.eligibility),
    }
  }
  if (kind === 'discovery') {
    const pid = str(opts.payerId)
    return {
      provider: name,
      providerId: safeSlug(opts.slug),
      ...out,
      payerId: pid,
      payerName: name,
      stediPayerName: pid ? name : '',
      payerMatchConfidence: pid ? 'high' : '',
      payerMatchSource: 'discovery',
      payerNeedsConfirmation: false,
    }
  }
  // 'typed' (and any unknown kind): no routable id anywhere, so the server re-resolves
  // the NEW name; if nothing matches, eligibility fails instead of checking the old payer.
  return {
    provider: name,
    providerId: safeSlug(opts.slug),
    ...out,
    payerName: name,
    payerMatchSource: 'patient_typed',
  }
}

// A discovered candidate {payerName, payerId, carrierId, ...} as a payer pick (K3).
export function candidatePayerPick(cand) {
  const c = cand || {}
  return payerPick({ kind: 'discovery', payerId: c.payerId, name: c.payerName, slug: c.carrierId })
}

/**
 * The payer pick a card scan implies, or null when the scan leaves the carrier alone:
 *   - the scan resolved a payer id  -> the scan's whole verdict replaces the key set;
 *   - the scan read only a name that differs from prev.provider -> a typed pick of it;
 *   - no carrier in the scan, or the same name with no id -> null (keep what is stored).
 */
export function scanPayerPick(extracted, prev) {
  const x = extracted || {}
  const pid = str(x.payerId)
  // ★★ PROD-1190: a card whose insurer has regional plans and that printed no region (Kaiser).
  //   orella-api (API-Paket 375) answers no payer id, payerNeedsConfirmation true and the regions
  //   under the additive key `regionCandidates`. The scan becomes the record the save path would
  //   store (ensure_stedi_payer_id's 'ambiguous_region' shape), so the screens' existing region
  //   card (regionChoices) asks at once. No payer id under any key: nothing routes before the
  //   pick, and the pick is regionPick's whole key set. An API without the key (374) answers as
  //   before and lands in the branches below unchanged.
  const regions = pid ? null : scanRegionCandidates(x)
  if (regions) {
    const name = str(x.cardInsurerName) || str(x.provider) || str(x.carrier)
    const p = prev || {}
    // The same insurer again over a record that already routes (a region picked before) keeps it.
    if (name && name.toLowerCase() === str(p.provider).toLowerCase() && PAYER_ID_KEYS.some((k) => str(p[k]))) return null
    return {
      provider: name,
      providerId: 'custom',
      ...cleared(),
      payerName: name,
      payerMatchSource: 'ambiguous_region',
      payerNeedsConfirmation: true,
      payerCandidates: regions,
    }
  }
  if (pid) {
    const name = str(x.payerName) || str(x.provider)
    return {
      provider: name,
      providerId: pid,
      ...cleared(),
      payerId: pid,
      stediPayerId: str(x.stediPayerId),
      primaryPayerId: str(x.primaryPayerId),
      payerName: str(x.payerName),
      stediPayerName: str(x.stediPayerName) || str(x.payerName),
      payerMatchConfidence: str(x.payerMatchConfidence),
      payerMatchSource: str(x.payerMatchSource),
      payerNeedsConfirmation: x.payerNeedsConfirmation === true,
      payerCandidates: Array.isArray(x.payerCandidates) ? x.payerCandidates : [],
      stediPayerEligibility: str(x.payerEligibility),
    }
  }
  const name = str(x.provider) || str(x.carrier)
  if (!name) return null
  if (name.toLowerCase() === str(prev && prev.provider).toLowerCase()) return null
  return payerPick({ kind: 'typed', name })
}

/**
 * The insurance section after applying discovered candidate `idx` over `ins` (Home).
 * Replaces the base's `providerId: cand.carrierId || ins.providerId`, which kept the old
 * routable id whenever the candidate had no registry slug.
 */
export function insuranceFromDiscoveryCandidate(ins, cand, idx, nowIso) {
  const i = ins || {}
  const c = cand || {}
  const pick = candidatePayerPick(c)
  return {
    ...i,
    completed: true,
    selfPay: false,
    coverageType: i.coverageType || 'commercial-insurance',
    planType: i.planType || 'commercial-insurance',
    ...pick,
    provider: pick.provider || i.provider || '',
    memberId: c.memberIdGuess || i.memberId || '',
    insurerName: c.payerName || i.insurerName || '',
    subscriberRelationship: i.subscriberRelationship || 'Self',
    discovery_result: {
      ...(i.discovery_result || {}),
      acknowledged: true,
      appliedCandidateIndex: idx,
      appliedAt: nowIso,
    },
  }
}

/**
 * L1 step 6 / D4 (PROD-188): the regions a patient must choose between, or null.
 *
 * ★★ THE API'S SHAPE (orella-api v243.268.0, rcm_service.ensure_stedi_payer_id): a carrier
 *   name that several regional payers share (Kaiser Permanente: Northern California,
 *   Southern California, Washington ...) and that the patient's state does not settle is
 *   stored with payerMatchSource 'ambiguous_region', payerNeedsConfirmation true, the
 *   in-state siblings as payerCandidates, and NO payer id under any key. "Yes, that's my
 *   plan" cannot answer that: there is no plan to confirm, and confirming leaves the record
 *   unroutable. The patient picks one candidate; the pick is a whole-key-set 'alternate'
 *   payerPick, saved by the screen's own insurance save.
 * Only candidates with both a payerId and a displayName are offered.
 */
// The four keys a routable payer id can live under (regionChoices' guard).
export const PAYER_ID_KEYS = Object.freeze(['payerId', 'payer_id', 'stediPayerId', 'primaryPayerId'])

export function regionChoices(ins) {
  const i = ins || {}
  if (str(i.payerMatchSource) !== 'ambiguous_region' || i.payerNeedsConfirmation !== true) return null
  if (PAYER_ID_KEYS.some((k) => str(i[k]))) return null
  const list = (Array.isArray(i.payerCandidates) ? i.payerCandidates : [])
    .filter((c) => c && str(c.payerId) && str(c.displayName))
  return list.length ? list : null
}

// PROD-1190: the regions a card scan answered (`regionCandidates`, orella-api API-Paket 375), or
// null. Only rows with both a payerId and a displayName, as regionChoices offers them.
export function scanRegionCandidates(extracted) {
  const x = extracted || {}
  const list = (Array.isArray(x.regionCandidates) ? x.regionCandidates : [])
    .filter((c) => c && str(c.payerId) && str(c.displayName))
    .map((c) => ({ payerId: str(c.payerId), displayName: str(c.displayName), eligibility: c.eligibility === undefined ? null : c.eligibility }))
  return list.length ? list : null
}

// PROD-1190: the request body of POST /ai/extract-card-data. `patientState` (the state of the
// patient's address, text only) is sent with an INSURANCE card when known: the server narrows a
// regional insurer's plans to that state, as the save path does from identification.state. An ID
// card, or no state, sends the body as before.
export function extractCardBody(image, cardType = 'ID', side = 'front', opts = {}) {
  const body = { image, cardType, side }
  const st = str(opts && opts.patientState).slice(0, 40)
  if (st && str(cardType).toLowerCase() === 'insurance') body.patientState = st
  return body
}

// The pick of one region candidate: the whole payer key set, through the owner.
export function regionPick(cand) {
  const c = cand || {}
  return payerPick({ kind: 'alternate', payerId: c.payerId, name: c.displayName, eligibility: c.eligibility })
}

// The payer keys a store holds that are neither undefined nor null, for a wire body.
// A clean onboarding store holds none, so it sends none (K5); a re-pick holds all (K1).
export function payerKeysForWire(ins) {
  const out = {}
  const i = ins || {}
  for (const k of PAYER_KEYS) {
    if (i[k] !== undefined && i[k] !== null) out[k] = i[k]
  }
  return out
}

// ★ PROD-431 item 91, patient push 3a: ONE message on an ambiguous record. An eligibility check
//   on a record that has no payer yet answers payer_not_mapped ("Setting up"), and that badge sat
//   above the region card that is the actual next step. While the region card shows, a
//   payer_not_mapped badge is not shown (every other class still is).
export function badgeHiddenByRegionCard(ins, eligibility) {
  if (!regionChoices(ins)) return false
  const rc = str(eligibility && (eligibility.resultClass || eligibility.status)).toLowerCase()
  return rc === 'payer_not_mapped'
}

// ★ PROD-431 (1), patient push 3a: an ambiguous_region record that ALSO carries a payer id was
//   settled by someone else (staff, through the md panel's documents save). It routes. There is
//   no region to pick and no match to confirm, so neither card is offered over it: the confirm
//   card's "Other close matches" would let a tap replace the staff's id just the same.
export function regionSettled(ins) {
  const i = ins || {}
  return str(i.payerMatchSource) === 'ambiguous_region' && PAYER_ID_KEYS.some((k) => str(i[k]))
}
